// @givmo/fundraisers-module — tolerant P2P management decode + display helpers (CPB-WEB4).
//
// The management overview is a singleton object, but callers may observe either that object or a
// defensive `{data: object}` wrapper. Its child lists are decoded with the shared `extractList`, so
// bare arrays and wrapped list shapes remain readable. Money is already an exact decimal STRING on
// the wire: helpers format it with digit-string math and never parse, sum, or rederive it.
//
// Team lifecycle is supplied by the generated management row. Refetched server
// status wins after every mutation; no client-held lifecycle override is needed.

import {
  formatMoneyDisplay,
  type P2pConfigOut,
  type P2pManagedLayerOut,
  type P2pManagedSupporterOut,
  type P2pManagedTeamOut,
} from '@givmo/api-client';
import { extractList } from '@givmo/api-client/list';
import type { BadgeProps } from '@givmo/design-system';

type BadgeTone = NonNullable<BadgeProps['tone']>;

export type ManagedP2pTeamRow = P2pManagedTeamOut;

/** Tolerantly decoded management overview consumed by the campaign detail page. */
export interface DecodedP2pOverview {
  config: P2pConfigOut;
  teams: ManagedP2pTeamRow[];
  supporters: P2pManagedSupporterOut[];
  /** FCM-BE33: the child layers' rows, oldest link first; `null` while the overview carries none (dark). */
  layers: P2pManagedLayerOut[] | null;
  /**
   * FCM-BE47: the requests to join waiting for an answer, for the tabs' counts: people's, and campaigns'
   * (`null` while the layers read dark). `null` from a backend that serves no requests.
   */
  pendingRequests: PendingRequests | null;
}

/** The waiting requests' counts, as served; a count the server leaves out is `null`, never 0. */
export interface PendingRequests {
  people: number;
  campaigns: number | null;
}

function count(value: unknown): number | null {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 ? value : null;
}

function decodePendingRequests(value: unknown): PendingRequests | null {
  if (!isRecord(value)) return null;
  const people = count(value.people);
  return people === null ? null : { people, campaigns: count(value.campaigns) };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function unwrapObjectEnvelope(value: unknown): Record<string, unknown> | null {
  if (!isRecord(value)) return null;
  return isRecord(value.data) ? value.data : value;
}

function isP2pConfig(value: unknown): value is P2pConfigOut {
  return (
    isRecord(value) &&
    typeof value.campaign_id === 'string' &&
    typeof value.teams_enabled === 'boolean' &&
    typeof value.supporter_teams_allowed === 'boolean' &&
    typeof value.join_open === 'boolean'
  );
}

/**
 * Decode a P2P management overview without trusting a single envelope representation. Invalid or
 * incomplete singleton bodies return `null`; malformed/missing child lists degrade independently to
 * `[]` through `extractList` rather than breaking the page.
 */
export function extractP2pOverview(body: unknown): DecodedP2pOverview | null {
  const overview = unwrapObjectEnvelope(body);
  if (overview === null || !isP2pConfig(overview.config)) return null;

  return {
    config: overview.config,
    teams: extractList<ManagedP2pTeamRow>(overview.teams, 'teams'),
    supporters: extractList<P2pManagedSupporterOut>(overview.supporters, 'supporters'),
    layers:
      overview.layers === undefined
        ? null
        : extractList<P2pManagedLayerOut>(overview.layers, 'layers').filter(
            (row) => isRecord(row) && typeof row.campaign_id === 'string',
          ),
    pendingRequests: decodePendingRequests(overview.pending_requests),
  };
}

/**
 * Dollar-marked display with digit-string formatting and no precision-losing conversion, always with
 * two decimals: the campaign's figures are the ones its public pages print, and the Campaign tab
 * previews those pages (the card and the leaderboard) in the donate app's own form ("$18,190.00
 * raised of $25,000.00 goal", `apps/donate/app/components/campaign-card.tsx`). The module's own
 * figures drop whole cents (`formatFundraiserMoney`); a preview never reads differently from
 * the page it previews.
 */
export function displayP2pMoney(value: string | null | undefined): string {
  if (value == null || value === '') return '—';
  const formatted = formatMoneyDisplay(value);
  return formatted === null ? value : `$${formatted}`;
}

function humanizeToken(value: string | null | undefined): string {
  const cleaned = (value ?? '').replace(/[_-]+/g, ' ').trim().toLowerCase();
  return cleaned.length === 0 ? '—' : cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
}

/** Plain-language team status; unknown future values are humanized instead of hidden. */
export function teamStatusLabel(status: string | null | undefined): string {
  return humanizeToken(status);
}

/** Visual tone only. Unknown/missing team states fail forward to neutral. */
export function teamStatusTone(status: string | null | undefined): BadgeTone {
  switch ((status ?? '').toLowerCase()) {
    case 'active':
      return 'success';
    case 'closed':
      return 'warning';
    case 'removed':
      return 'danger';
    default:
      return 'neutral';
  }
}

/** Plain-language supporter status; unknown future values are humanized instead of hidden. */
export function supporterStatusLabel(status: string | null | undefined): string {
  return humanizeToken(status);
}

/** Visual tone only. Unknown/missing supporter states fail forward to neutral. */
export function supporterStatusTone(status: string | null | undefined): BadgeTone {
  switch ((status ?? '').toLowerCase()) {
    case 'active':
      return 'success';
    case 'removed':
      return 'danger';
    default:
      return 'neutral';
  }
}

/** Human label for how a supporter joined; additive source tokens are humanized. */
export function supporterSourceLabel(source: string | null | undefined): string {
  switch ((source ?? '').toLowerCase()) {
    case 'self_serve':
      return 'Self-serve';
    case 'event_registration':
      return 'Event registration';
    default:
      return humanizeToken(source);
  }
}

/** Honest public-page availability label, distinct from the management moderation status. */
export function pageActiveLabel(value: boolean | null | undefined): string {
  if (value == null) return '—';
  return value ? 'Active' : 'Inactive';
}

/** Visual tone for public-page availability; missing values fail closed to neutral. */
export function pageActiveTone(value: boolean | null | undefined): BadgeTone {
  return value === true ? 'success' : 'neutral';
}

/** Localized joined date; malformed/missing timestamps render an em dash instead of throwing. */
export function displayP2pDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}
